import React from 'react'
import { observer } from 'mobx-react'
import _ from 'lodash'

import { makeSmallImgUrl } from '@v2/utils/global-utils'

import SYMBOL_ICO from '@assets/images/icon/meta_image/symbol.png'

/**
 * 이미지 컴포넌트의 src 모드 속성
 * 기본값은 default
 *
 * 추가 옵션
 * - mixed: 이미지 src 에 small 키워드를 붙이고 이미지가 없을때 대체 이미지로 원본 src 를 사용.
 *          이미지 src URL 의 마지막 이미지 이름 앞에 small 키워드를 붙여 준다.
 * - 예시:
 *   - src: https://d1i7hydgfclv7m.cloudfront.net/data_center/user_profile/cc26444e-a91b-42c0-899d-2203035fb268.webp
 *   - small src: https://d1i7hydgfclv7m.cloudfront.net/data_center/user_profile/small/cc26444e-a91b-42c0-899d-2203035fb268.webp
 */
export type ImgSrcMode = 'default' | 'small'

interface Props extends React.ImgHTMLAttributes<HTMLImageElement> {
  // 대체 이미지 URL
  altSrc?: string
  srcMode?: ImgSrcMode
  onLoad?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void
}

/**
 * HTML img 태그를 확장한 컴포넌트
 *
 * 이미지 로딩 실패시 대체 이미지를 사용할 수 있도록 onError 이벤트 핸들러를 추가하였다.
 *
 * srcMode 속성을 통해 src URL 을 변경할 수 있으며 현재 small 모드의 경우 이미지 URL 의 마지막 이미지 이름 앞에 small 키워드를 붙여 준다.
 *
 * 기본 HTML 의 img 태그와 동일하게 사용할 수 있다.
 *
 * src 및 altSrc 가 모두 없을 경우 기본 이미지로 승부사마크를 사용한다.
 *
 * @param props Props
 */
const AdvImg: React.FC<Props> = observer((props): React.ReactElement => {
  const { src = SYMBOL_ICO, altSrc, srcMode = 'default', alt } = props

  let imgSrc = src
  let altImgSrc = altSrc
  if (srcMode === 'small') {
    imgSrc = makeSmallImgUrl(src)
    if (altSrc == null) {
      altImgSrc = src
    }
  }

  if (_.isEmpty(imgSrc)) {
    imgSrc = SYMBOL_ICO
  }
  if (_.isEmpty(altImgSrc)) {
    altImgSrc = SYMBOL_ICO
  }

  return (
    <img
      {...props}
      src={imgSrc}
      alt={alt}
      onError={(e: React.SyntheticEvent<HTMLImageElement, Event>): void => {
        if (props.onError) {
          props.onError(e)
        } else {
          const target = e.target as HTMLImageElement
          target.onerror = null
          target.src = altImgSrc ?? ''
        }
      }}
    />
  )
})

export default AdvImg
